import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ClipboardList, Calendar as CalendarIcon, Loader2, Download, Eye, Package, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);
const weekdayIt = (iso) => ['Domenica','Lunedì','Martedì','Mercoledì','Giovedì','Venerdì','Sabato'][new Date(iso).getDay()];
const fmtDate = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function RaccoltaPage() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/raccolta/daily`, { params: { date } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPdf = async (download = false) => {
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/raccolta/pdf`, {
        params: { date, ...(download ? { download: true } : {}) },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Raccolta_${date.replace(/-/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Foglio raccolta scaricato');
      } else {
        window.open(url, '_blank');
      }
    } catch {
      toast.error('Errore generazione PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const totalCompanies = data?.total_companies || 0;
  const totalColli = data?.total_colli || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ClipboardList size={24} className="text-primary" />
            Foglio Raccolta
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Riepilogo operativo magazzino — ordini confermati per data
          </p>
        </div>
      </div>

      {/* Date selector & actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                <CalendarIcon size={12} className="inline mr-1" />
                Data ordine
              </Label>
              <Input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="h-10 w-44"
                data-testid="raccolta-date-input"
              />
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => setDate(today())}
              data-testid="raccolta-today-btn"
            >Oggi</Button>
            <Button
              variant="ghost" size="icon"
              onClick={fetchData}
              disabled={loading}
              title="Ricarica"
              data-testid="raccolta-refresh-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline" size="sm"
              onClick={() => openPdf(false)}
              disabled={pdfLoading || !totalCompanies}
              data-testid="raccolta-preview-btn"
            >
              {pdfLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Eye size={13} className="mr-1.5" />}
              Anteprima PDF
            </Button>
            <Button
              size="sm"
              onClick={() => openPdf(true)}
              disabled={pdfLoading || !totalCompanies}
              data-testid="raccolta-download-btn"
            >
              {pdfLoading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />}
              Scarica PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary banner */}
      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 px-6 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wider">
            {weekdayIt(date)} {fmtDate(date)}
          </p>
          <h2 className="text-3xl font-bold text-primary mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            RACCOLTA DEL {fmtDate(date)}
          </h2>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold">{totalCompanies}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Clienti</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-primary">{totalColli}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Colli Totali</p>
          </div>
        </div>
      </div>

      {/* Companies list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-primary" size={32} /></div>
      ) : !data?.companies?.length ? (
        <Card>
          <CardContent className="text-center py-16">
            <Package size={48} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">Nessun ordine confermato</h3>
            <p className="text-sm text-muted-foreground">
              Non ci sono ordini in stato "Confermato" per il {fmtDate(date)}.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Suggerimento: conferma gli ordini dalla pagina dettaglio per includerli nella raccolta.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.companies.map((c, ci) => {
            const customerColli = c.items.reduce((s, it) => s + it.total_colli, 0);
            return (
              <Card key={c.company_name + ci} data-testid={`raccolta-company-${ci}`}>
                <CardHeader className="pb-2 bg-primary/5 border-b">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-baseline gap-3">
                      <CardTitle className="text-lg font-bold uppercase tracking-wide">{c.company_name}</CardTitle>
                      {c.customer_code && (
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground">
                          COD. {c.customer_code}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      <span className="font-bold text-primary text-lg">{customerColli}</span> colli
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/30 border-b text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2 w-28">Codice</th>
                        <th className="text-left px-4 py-2">Prodotto</th>
                        <th className="text-center px-4 py-2 w-24">Quantità</th>
                        <th className="text-center px-4 py-2 w-24">N° Colli</th>
                        <th className="text-left px-4 py-2 w-40">N° Ordine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.items.map((it, i) => {
                        const qty = it.total_quantity;
                        const qtyStr = qty ? (Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/\.?0+$/, '')) : '-';
                        const orders = (it.order_numbers || []).join(', ');
                        return (
                          <tr key={it.code + i} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="px-4 py-2.5 font-mono font-semibold text-primary">{it.code}</td>
                            <td className="px-4 py-2.5">{it.description || <span className="text-muted-foreground italic">(nessuna descrizione)</span>}</td>
                            <td className="px-4 py-2.5 text-center">{qtyStr}</td>
                            <td className="px-4 py-2.5 text-center font-bold text-lg">{it.total_colli}</td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">{orders || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
